import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import SecretKeyDecryption from "./secretKeyDecryption";
import { passwordEncrypt } from "./passwordEncryption"; 
import "./hm-theme.css";

function CompanyProfile({selectedOrgcode}) {
  const { fetchSecretKey } = SecretKeyDecryption();
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  
  const [orgProfile, setOrgProfile] = useState({
    username:"",
    companyname:"",
    companycode:"",
    contact:"",
    description:"",
    pan:"",
    gstin:"",
    tan:"",
    addressLine1:"",
    addressLine2:"",
    city:"",
    pincode:"",
    state:"",
    createddate:"",
    domainName:"",
    pilotOrganization: false,
    companyProfileImage:{
      image:null,
      imageMeta:""
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/companydetails/getCompanyDetailsWithProfileImageByCompanycode/companycode=${selectedOrgcode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userProfileDetails = response.data;
        setOrgProfile(userProfileDetails);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    };

    fetchProfile();
  }, []);

  function getBase64FromFile(file) {
    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        const [metadata, content] = reader.result.split(",");
        resolve({ metadata, content });
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  }

  const handleImageChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const convertedImage = await getBase64FromFile(event.target.files[0]);
      setOrgProfile((prevState) => ({
        ...prevState,
        companyProfileImage: {
          imageMeta: convertedImage.metadata,
          image: convertedImage.content,
        },
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await axios.post(`/api/companydetails/update`, orgProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Company Profile Updated Successfully");
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, field) => {
    let actualValue = event.target.value;
    setOrgProfile({ ...orgProfile, [field]: actualValue });
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {  
      const decryptedSecretKey = await fetchSecretKey();
      if (!decryptedSecretKey) {
        setLoading(false);
        return;
      }
      const encryptedPassword = await passwordEncrypt(password, decryptedSecretKey);
      const body = {
        id: orgProfile.userid,
        password: encryptedPassword,
      };

      await axios.post(`/api/accounts/update-password`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Password Updated Successfully");
      setPassword("");
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();
  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handlePilotOrgChange = (e) => {
    const { checked } = e.target;
    setOrgProfile((prevState) => ({
      ...prevState,
      pilotOrganization: checked
    }));
  };

  return (
    <div>
      {loading && <div className="loading"></div>}

      {/* Company Profile Card */}
      <div className="hm-card mb-4">
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">domain</i>
            Company Profile
          </h3>
          {orgProfile.companycode && (
            <span className="hm-badge hm-badge-primary">Code: {orgProfile.companycode}</span>
          )}
        </div>
        <div className="hm-card-body p-4">
          <div className="row g-4 align-items-start">

            {/* Profile Avatar Column */}
            <div className="col-md-3 d-flex flex-column align-items-center text-center border-end border-light pe-md-4">
              <div className="position-relative mb-3">
                {orgProfile.companyProfileImage?.image ? (
                  <img
                    className="shadow-sm border"
                    src={`${orgProfile.companyProfileImage?.imageMeta},${orgProfile.companyProfileImage?.image}`}
                    alt="Company Profile"
                    style={{
                      width: "140px",
                      height: "140px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      width: "140px",
                      height: "140px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #eef4ff 0%, #dbeafe 100%)",
                      color: "#2f3a88",
                    }}
                  >
                    <i className="material-icons" style={{ fontSize: "64px" }}>business</i>
                  </div>
                )}
              </div>

              <label htmlFor="company-logo-input" className="btn btn-outline-primary btn-sm rounded-pill px-3 cursor-pointer mb-2">
                <i className="material-icons align-middle me-1" style={{ fontSize: "16px" }}>photo_camera</i>
                Choose Logo
              </label>
              <input
                type="file"
                id="company-logo-input"
                className="d-none"
                onChange={handleImageChange}
                accept="image/*"
              />
              <span className="text-muted small">JPG, PNG or GIF. Max size 2MB.</span>
            </div>

            {/* Form Fields Column */}
            <div className="col-md-9 ps-md-4">
              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="companyname">Company Name</label>
                    <input
                      className="form-control"
                      id="companyname"
                      type="text"
                      value={orgProfile.companyname}
                      onChange={(e) => handleChange(e, "companyname")}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="companycode">Company Code</label>
                    <input
                      className="form-control bg-light"
                      id="companycode"
                      type="text"
                      value={orgProfile.companycode}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="username">Email ID</label>
                    <input
                      className="form-control bg-light"
                      id="username"
                      type="email"
                      value={orgProfile.username}
                      readOnly
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="contact">Contact Number</label>
                    <input
                      className="form-control"
                      id="contact"
                      type="text"
                      value={orgProfile.contact}
                      onChange={(e) => handleChange(e, "contact")}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="domainName">Domain Name</label>
                    <input
                      className="form-control"
                      id="domainName"
                      type="text"
                      value={orgProfile.domainName}
                      onChange={(e) => handleChange(e, "domainName")}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="gstin">GSTIN</label>
                    <input
                      className="form-control"
                      id="gstin"
                      type="text"
                      value={orgProfile.gstin}
                      onChange={(e) => handleChange(e, "gstin")}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="pan">PAN</label>
                    <input
                      className="form-control"
                      id="pan"
                      type="text"
                      value={orgProfile.pan}
                      onChange={(e) => handleChange(e, "pan")}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="tan">TAN</label>
                    <input
                      className="form-control"
                      id="tan"
                      type="text"
                      value={orgProfile.tan}
                      onChange={(e) => handleChange(e, "tan")}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="addressLine1">Address Line 1</label>
                    <input
                      className="form-control"
                      id="addressLine1"
                      type="text"
                      value={orgProfile.addressLine1}
                      onChange={(e) => handleChange(e, "addressLine1")}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="addressLine2">Address Line 2</label>
                    <input
                      className="form-control"
                      id="addressLine2"
                      type="text"
                      value={orgProfile.addressLine2}
                      onChange={(e) => handleChange(e, "addressLine2")}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="city">City</label>
                    <input
                      className="form-control"
                      id="city"
                      type="text"
                      value={orgProfile.city}
                      onChange={(e) => handleChange(e, "city")}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="pincode">Pincode</label>
                    <input
                      className="form-control"
                      id="pincode"
                      type="text"
                      value={orgProfile.pincode}
                      onChange={(e) => handleChange(e, "pincode")}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small text-secondary" htmlFor="state">State</label>
                    <input
                      className="form-control"
                      id="state"
                      type="text"
                      value={orgProfile.state}
                      onChange={(e) => handleChange(e, "state")}
                    />
                  </div>
                </div>

                <div className="form-check form-switch mt-3 mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="pilotOrganization"
                    checked={orgProfile.pilotOrganization || false}
                    onChange={handlePilotOrgChange}
                  />
                  <label className="form-check-label fw-semibold text-secondary" htmlFor="pilotOrganization">
                    Pilot Organization
                  </label>
                </div>

                <div className="pt-2">
                  <button type="submit" className="btn-hm-primary">
                    <i className="material-icons" style={{ fontSize: "1rem" }}>save</i>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Update Password Card */}
      <div className="hm-card">
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">lock</i>
            Update Password
          </h3>
        </div>
        <div className="hm-card-body p-4">
          <form onSubmit={handlePasswordSubmit}>
            <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label fw-semibold small text-secondary" htmlFor="password">New Password</label>
                  <input
                    className="form-control"
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="text-center mt-3">
                  <button type="submit" className="btn-hm-primary">
                    <i className="material-icons" style={{ fontSize: "1rem" }}>key</i>
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CompanyProfile;